<template>
  <div class="privacy-policy">
    <div class="language-tabs">
      <div 
        class="language-tab" 
        :class="{ active: activeLanguage === 'english' }" 
        @click="setActiveLanguage('english')"
      >English</div>
      <div 
        class="language-tab" 
        :class="{ active: activeLanguage === 'italian' }" 
        @click="setActiveLanguage('italian')"
      >Italiano</div>
      <div 
        class="language-tab" 
        :class="{ active: activeLanguage === 'chinese' }" 
        @click="setActiveLanguage('chinese')"
      >中文</div>
    </div>

    <!-- English Version -->
    <div v-if="activeLanguage === 'english'" class="policy-content">
      <h1>Chicchi Nails Privacy Policy</h1>
      <p class="last-updated">Last Updated: 15/04/2025</p>

      <p>Welcome to Chicchi Nails nail salon reservation system. We value your privacy.</p>

      <h2>1. Information We Collect</h2>
      <p>To provide our services, we collect the following data:</p>
      <ul>
        <li><strong>Customer Data:</strong> Name, contact information, service preferences, appointment history</li>
        <li><strong>Staff Data:</strong> Name, contact information, work schedules, skills</li>
        <li><strong>Appointment Data:</strong> Date/time, services requested, special notes</li>
        <li><strong>Payment Data:</strong> Information needed to process payments (this data is handled directly by the payment processor)</li>
      </ul>

      <h2>2. How We Use Information</h2>
      <p>We use collected data to:</p>
      <ul>
        <li>Manage and schedule appointments</li>
        <li>Provide personalized services to customers</li>
        <li>Manage loyalty programs and promotions</li>
        <li>Improve our service quality</li>
        <li>Communicate with customers and staff</li>
        <li>Comply with legal obligations</li>
      </ul>

      <h2>3. Data Sharing</h2>
      <p>Customer and staff data is not sold or shared with third parties, except in these cases:</p>
      <ul>
        <li>With service providers assisting our operations (e.g., cloud hosting, payment services)</li>
        <li>When required by law or to protect our rights</li>
        <li>In case of business sale or transfer (the buyer would receive data only to continue the service)</li>
      </ul>

      <h2>4. Data Security</h2>
      <p>We implement technical and organizational measures to protect personal data from unauthorized access, alteration, or destruction.</p>

      <h2>5. User Rights</h2>
      <p>In accordance with applicable laws, you have the right to:</p>
      <ul>
        <li>Access your personal data</li>
        <li>Request correction of inaccurate data</li>
        <li>Request deletion of your data</li>
        <li>Restrict processing of your data</li>
        <li>Object to processing of your data</li>
        <li>Request data portability</li>
      </ul>

      <h2>6. Data Retention</h2>
      <p>We retain personal data only for as long as necessary to provide requested services and fulfill legal obligations.</p>

      <h2>7. Policy Changes</h2>
      <p>We may update this policy periodically. Changes will be effective upon posting.</p>

      <div class="contact">
        <h2>Contact Us</h2>
        <p>For questions about this policy or to exercise your privacy rights, contact us at:</p>
        <p>Email: <a href="mailto:manicure.booking@gmail.com">manicure.booking@gmail.com</a></p>
      </div>
    </div>

    <!-- Italian Version -->
    <div v-if="activeLanguage === 'italian'" class="policy-content">
      <h1>Informativa sulla privacy di Chicchi Nails</h1>
      <p class="last-updated">Ultimo aggiornamento: 15/04/2025</p>

      <p>Benvenuti al sistema di prenotazione appuntamenti del salone di bellezza Chicchi Nails. La tua privacy è per noi una cosa seria.</p>

      <h2>1. Informazioni che raccogliamo</h2>
      <p>Per fornire i nostri servizi, raccogliamo i seguenti dati:</p>
      <ul>
        <li><strong>Dati dei clienti:</strong> nome, cognome, informazioni di contatto, preferenze di servizio, storico appuntamenti</li>
        <li><strong>Dati dei dipendenti:</strong> nome, cognome, informazioni di contatto, orari di lavoro, competenze</li>
        <li><strong>Dati degli appuntamenti:</strong> data/ora, servizi richiesti, note speciali</li>
        <li><strong>Dati di pagamento:</strong> informazioni necessarie per elaborare i pagamenti (questi dati sono gestiti direttamente dal processore di pagamenti)</li>
      </ul>

      <h2>2. Come utilizziamo le informazioni</h2>
      <p>Utilizziamo i dati raccolti per:</p>
      <ul>
        <li>Gestire e organizzare gli appuntamenti</li>
        <li>Fornire servizi personalizzati ai clienti</li>
        <li>Gestire i programmi fedeltà e promozioni</li>
        <li>Migliorare la qualità dei nostri servizi</li>
        <li>Comunicare con clienti e dipendenti</li>
        <li>Rispettare gli obblighi legali</li>
      </ul>

      <h2>3. Condivisione dei dati</h2>
      <p>I dati dei clienti e dei dipendenti non vengono venduti o condivisi con terze parti, ecceto nei seguenti casi:</p>
      <ul>
        <li>Con fornitori di servizi che ci assistono nelle operazioni (es. hosting cloud, servizi di pagamento)</li>
        <li>Quando richiesto dalla legge o per proteggere i nostri diritti</li>
        <li>In caso di vendita o trasferimento dell'attività (il acquirente riceverebbe i dati solo per continuare il servizio)</li>
      </ul>

      <h2>4. Sicurezza dei dati</h2>
      <p>Adottiamo misure tecniche e organizzative per proteggere i dati personali da accessi non autorizzati, alterazioni o distruzione.</p>

      <h2>5. Diritti degli utenti</h2>
      <p>In conformità al GDPR, hai diritto a:</p>
      <ul>
        <li>Accedere ai tuoi dati personali</li>
        <li>Richiedere la correzione di dati inesatti</li>
        <li>Richiedere la cancellazione dei tuoi dati</li>
        <li>Limitare il trattamento dei tuoi dati</li>
        <li>Opporti al trattamento dei tuoi dati</li>
        <li>Richiedere la portabilità dei dati</li>
      </ul>

      <h2>6. Conservazione dei dati</h2>
      <p>Conserviamo i dati personali solo per il tempo necessario a fornire i servizi richiesti e per adempiere agli obblighi legali.</p>

      <h2>7. Modifiche all'informativa</h2>
      <p>Potremmo aggiornare periodicamente questa politica. Le modifiche saranno effettive dal momento della pubblicazione.</p>

      <div class="contact">
        <h2>Contatti</h2>
        <p>Per domande su questa informativa o per esercitare i tuoi diritti sulla privacy, contattaci a:</p>
        <p>Email: <a href="mailto:manicure.booking@gmail.com">manicure.booking@gmail.com</a></p>
      </div>
    </div>

    <!-- Chinese Version -->
    <div v-if="activeLanguage === 'chinese'" class="policy-content">
      <h1>Chicchi Nails 隐私政策</h1>
      <p class="last-updated">最后更新日期: 15/04/2025</p>

      <p>欢迎使用Chicchi Nails美甲沙龙预约系统。我们重视您的隐私保护。</p>

      <h2>1. 我们收集的信息</h2>
      <p>为了提供服务，我们会收集以下数据:</p>
      <ul>
        <li><strong>客户数据:</strong> 姓名、联系方式、服务偏好、预约历史</li>
        <li><strong>员工数据:</strong> 姓名、联系方式、工作时间、技能</li>
        <li><strong>预约数据:</strong> 日期/时间、所需服务、特殊备注</li>
        <li><strong>支付数据:</strong> 处理支付所需信息(这些数据由支付处理器直接处理)</li>
      </ul>

      <h2>2. 信息使用方式</h2>
      <p>我们收集的数据用于:</p>
      <ul>
        <li>管理和安排预约</li>
        <li>为客户提供个性化服务</li>
        <li>管理会员计划和促销活动</li>
        <li>提高服务质量</li>
        <li>与客户和员工沟通</li>
        <li>履行法律义务</li>
      </ul>

      <h2>3. 数据共享</h2>
      <p>客户和员工数据不会被出售或与第三方共享，除非:</p>
      <ul>
        <li>协助我们运营的服务提供商(如云托管、支付服务)</li>
        <li>法律要求或为保护我们的权利</li>
        <li>业务出售或转让时(买家仅会为继续服务而接收数据)</li>
      </ul>

      <h2>4. 数据安全</h2>
      <p>我们采取技术和组织措施保护个人数据免受未经授权的访问、更改或破坏。</p>

      <h2>5. 用户权利</h2>
      <p>根据相关法律法规，您有权:</p>
      <ul>
        <li>访问您的个人数据</li>
        <li>要求更正不准确数据</li>
        <li>要求删除您的数据</li>
        <li>限制处理您的数据</li>
        <li>反对处理您的数据</li>
        <li>要求数据可携带</li>
      </ul>

      <h2>6. 数据保留</h2>
      <p>我们仅在提供所需服务和履行法律义务的必要时间内保留个人数据。</p>

      <h2>7. 政策变更</h2>
      <p>我们可能会定期更新本政策。更新后的政策将在网站上发布，重大变更时我们会通知您。</p>

      <div class="contact">
        <h2>联系我们</h2>
        <p>如对本政策有任何疑问或想行使您的隐私权利，请联系:</p>
        <p>邮箱: <a href="mailto:manicure.booking@gmail.com">manicure.booking@gmail.com</a></p>
      </div>
    </div>
    
    <div class="back-button">
      <el-button type="primary" @click="goBack">Torna alla Home</el-button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PrivacyPolicy',
  data() {
    return {
      activeLanguage: 'italian'
    };
  },
  methods: {
    setActiveLanguage(language) {
      this.activeLanguage = language;
    },
    goBack() {
      this.$router.push('/');
    }
  }
}
</script>

<style scoped>
.privacy-policy {
  font-family: Arial, sans-serif; 
  line-height: 1.6; 
  max-width: 1000px; 
  margin: 0 auto; 
  padding: 20px;
}

.language-tabs { 
  display: flex; 
  margin-bottom: 20px; 
  border-bottom: 1px solid #ddd; 
}

.language-tab { 
  padding: 10px 20px; 
  cursor: pointer; 
  background: #f1f1f1; 
  margin-right: 5px; 
  border-radius: 5px 5px 0 0; 
}

.language-tab.active { 
  background: #ff6b88; 
  color: white; 
}

.policy-content { 
  display: block; 
}

h1 { 
  color: #ff6b88; 
}

h2 { 
  color: #555; 
  margin-top: 25px; 
}

.contact { 
  background-color: #f8f8f8; 
  padding: 15px; 
  border-radius: 5px; 
  margin-top: 30px; 
}

.last-updated { 
  font-style: italic; 
  color: #666; 
  margin-bottom: 20px; 
}

.back-button {
  margin-top: 30px;
  text-align: center;
}

@media (max-width: 768px) {
  .privacy-policy {
    padding: 15px;
  }
  
  .language-tab {
    padding: 8px 15px;
    font-size: 14px;
  }
  
  h1 {
    font-size: 24px;
  }
  
  h2 {
    font-size: 20px;
  }
}
</style>
